import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import type { CompanyUser } from '@/types';
import { ASSIGNABLE_ROLES, ROLES, isCompanyAdmin, isManager } from '@/types';
import {
  Users, Plus, X, Loader2, Shield, KeyRound, UserCheck, UserX, AlertTriangle, Pencil,
} from 'lucide-react';
import { sanitizePhoneInput, validatePhone, UK_PHONE_ERROR } from '@/utils/mobileValidation';
import { translateRole } from '@/utils/translateStatus';
import { getCurrencySymbol } from '@/utils/currency';
import { authApi } from '@/services/api';
import { isPakistan } from '@/data/locales';

type FormState = {
  full_name: string;
  email: string;
  username: string;
  mobile: string;
  password: string;
  role: string;
  daily_expense_limit: string;
  shift_expense_limit: string;
  max_discount_pct: string;
  can_override_min_price: boolean;
  can_process_returns: boolean;
  can_add_expenses: boolean;
  can_update_item_prices: boolean;
  can_create_items: boolean;
  max_refund_amount: string;
  max_refund_count: string;
  salary: string;
};

const EMPTY_FORM: FormState = {
  full_name: '',
  email: '',
  username: '',
  mobile: '',
  password: '',
  role: ROLES.CASHIER,
  daily_expense_limit: '',
  shift_expense_limit: '',
  max_discount_pct: '',
  can_override_min_price: false,
  can_process_returns: true,
  can_add_expenses: true,
  can_update_item_prices: false,
  can_create_items: false,
  max_refund_amount: '',
  max_refund_count: '',
  salary: '',
};

export default function TeamUsersTab() {
  const { t } = useTranslation();
  const { user: currentUser, role: currentRole } = useAuthStore();
  const isMgr = isManager(currentRole);
  const allowedAssignableRoles = isMgr ? [ROLES.CASHIER] : ASSIGNABLE_ROLES;
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [resetTarget, setResetTarget] = useState<CompanyUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
  });
  const countryCode = profile?.country || 'GB';

  const { data: limits } = useQuery({
    queryKey: ['userLimits'],
    queryFn: async () => {
      const res = await usersApi.getLimits();
      return res.data.message as { max_users: number | null; current_users: number; can_add_more: boolean };
    },
  });

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['companyUsers'],
    queryFn: async () => {
      const res = await usersApi.list();
      return res.data.message as CompanyUser[];
    },
  });

  const createMutation = useMutation({
    mutationFn: () => usersApi.create({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      password: form.password,
      role: form.role,
      username: form.username.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      daily_expense_limit: parseFloat(form.daily_expense_limit) || 0,
      shift_expense_limit: parseFloat(form.shift_expense_limit) || 0,
      max_discount_pct: parseFloat(form.max_discount_pct) || 0,
      can_override_min_price: form.can_override_min_price,
      can_process_returns: form.can_process_returns,
      can_add_expenses: form.can_add_expenses,
      can_update_item_prices: form.can_update_item_prices,
      can_create_items: form.can_create_items,
      max_refund_amount: parseFloat(form.max_refund_amount) || 0,
      max_refund_count: parseInt(form.max_refund_count, 10) || 0,
      salary: parseFloat(form.salary) || 0,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companyUsers'] });
      qc.invalidateQueries({ queryKey: ['userLimits'] });
      setShowModal(false);
      setForm(EMPTY_FORM);
      setFormError('');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg || 'Failed to create user');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      name: string;
      role?: string;
      full_name?: string;
      mobile?: string;
      daily_expense_limit?: number;
      shift_expense_limit?: number;
      max_discount_pct?: number;
      can_override_min_price?: boolean;
      can_process_returns?: boolean;
      can_add_expenses?: boolean;
      can_update_item_prices?: boolean;
      can_create_items?: boolean;
      max_refund_amount?: number;
      max_refund_count?: number;
      salary?: number;
    }) => usersApi.update(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companyUsers'] });
      setEditingUser(null);
      if (showModal) setShowModal(false);
    },
  });

  const setActiveMutation = useMutation({
    mutationFn: ({ name, is_active }: { name: string; is_active: boolean }) =>
      usersApi.setActive({ name, is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companyUsers'] });
      qc.invalidateQueries({ queryKey: ['userLimits'] });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () => usersApi.resetPassword({ name: resetTarget!.name, new_password: newPassword }),
    onSuccess: () => {
      setResetTarget(null);
      setNewPassword('');
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.full_name.trim()) {
      setFormError('Full name is required.');
      return;
    }
    if (form.mobile.trim()) {
      const mobErr = validatePhone(form.mobile.trim(), countryCode, { required: false });
      if (mobErr) {
        setFormError(mobErr);
        return;
      }
    }
    if (editingUser) {
      updateMutation.mutate({
        name: editingUser.name,
        full_name: form.full_name.trim(),
        role: form.role,
        mobile: form.mobile.trim() || undefined,
        daily_expense_limit: parseFloat(form.daily_expense_limit) || 0,
        shift_expense_limit: parseFloat(form.shift_expense_limit) || 0,
        max_discount_pct: parseFloat(form.max_discount_pct) || 0,
        can_override_min_price: form.can_override_min_price,
        can_process_returns: form.can_process_returns,
        can_add_expenses: form.can_add_expenses,
        can_update_item_prices: form.can_update_item_prices,
        can_create_items: form.can_create_items,
        max_refund_amount: parseFloat(form.max_refund_amount) || 0,
        max_refund_count: parseInt(form.max_refund_count, 10) || 0,
        salary: parseFloat(form.salary) || 0,
      });
      setShowModal(false);
      setEditingUser(null);
      setForm(EMPTY_FORM);
    } else {
      if (!form.email.trim() || !form.password) {
        setFormError('Email and password are required.');
        return;
      }
      if (form.password.length < 6) {
        setFormError('Password must be at least 6 characters.');
        return;
      }
      createMutation.mutate();
    }
  };

  const handleUsernameBlur = async () => {
    if (!form.username.trim()) {
      setUsernameStatus('idle');
      setUsernameSuggestions([]);
      return;
    }
    setUsernameStatus('checking');
    try {
      const res = await usersApi.checkUsernameAvailability({ username: form.username.trim() });
      const data = res.data.message as { available: boolean; suggestions: string[] };
      if (data.available) {
        setUsernameStatus('available');
        setUsernameSuggestions([]);
      } else {
        setUsernameStatus('unavailable');
        setUsernameSuggestions(data.suggestions || []);
      }
    } catch (e) {
      console.error(e);
      setUsernameStatus('idle');
    }
  };

  const limitLabel = limits
    ? `${limits.current_users} / ${limits.max_users ?? 'Unlimited'} users`
    : '';

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('users.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('users.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {limits && (
              <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg font-medium">
                {limitLabel}
              </span>
            )}
            <button
              type="button"
              onClick={() => { setEditingUser(null); setForm(EMPTY_FORM); setShowModal(true); setFormError(''); }}
              disabled={limits?.can_add_more === false}
              className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> {t('users.add')}
            </button>
          </div>
        </div>

        {limits?.can_add_more === false && (
          <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 mb-4 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {t('users.userLimitReached')}{' '}
              <Link to="/support" className="underline font-medium">{t('users.upgradePlan')}</Link> {t('users.addMoreUsers')}
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading')}
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('users.empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-3 font-medium">{t('common.name')}</th>
                  <th className="pb-3 font-medium">{t('users.username')}</th>
                  <th className="pb-3 font-medium">{t('common.email')}</th>
                  <th className="pb-3 font-medium">{t('users.role')}</th>
                  <th className="pb-3 font-medium">{t('common.status')}</th>
                  <th className="pb-3 font-medium text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => {
                  const isSelf = row.user === currentUser?.name || row.email === currentUser?.email;
                  const isAdmin = isCompanyAdmin(row.role);
                  return (
                    <tr key={row.name} className="border-b border-gray-50 last:border-0">
                      <td className="py-3 pr-4">
                        <p className="font-medium text-gray-900">{row.full_name}</p>
                        {isSelf && <span className="text-xs text-primary-600">{t('users.you')}</span>}
                      </td>
                      <td className="py-3 pr-4 text-gray-600">{row.username || '-'}</td>
                      <td className="py-3 pr-4 text-gray-600">{row.email}</td>
                      <td className="py-3 pr-4">
                        {isAdmin ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium bg-primary-50 text-primary-700 px-2 py-1 rounded">
                            <Shield className="w-3 h-3" /> {translateRole(row.role, t)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-50 text-gray-700 px-2 py-1 rounded border border-gray-200">
                            {translateRole(row.role, t)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {row.is_active ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                            <UserCheck className="w-3.5 h-3.5" /> {t('common.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                            <UserX className="w-3.5 h-3.5" /> {t('users.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isMgr && isAdmin ? null : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingUser(row);
                                  setForm({
                                    full_name: row.full_name,
                                    email: row.email,
                                    username: row.username || '',
                                    mobile: row.mobile || '',
                                    password: '',
                                    role: row.role,
                                    daily_expense_limit: row.daily_expense_limit?.toString() || '',
                                    shift_expense_limit: row.shift_expense_limit?.toString() || '',
                                    max_discount_pct: row.max_discount_pct?.toString() || '',
                                    can_override_min_price: !!row.can_override_min_price,
                                    can_process_returns: row.can_process_returns !== false,
                                    can_add_expenses: row.can_add_expenses !== false,
                                    can_update_item_prices: !!row.can_update_item_prices,
                                    can_create_items: !!row.can_create_items,
                                    max_refund_amount: row.max_refund_amount?.toString() || '',
                                    max_refund_count: row.max_refund_count?.toString() || '',
                                    salary: row.salary?.toString() || '',
                                  });
                                  setFormError('');
                                  setShowModal(true);
                                }}
                                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                                title="Edit User"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              {/* SD-UK-054: never offer reset for Owner/Company Admin */}
                              {!isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => { setResetTarget(row); setNewPassword(''); }}
                                  className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                                  title="Reset password"
                                >
                                  <KeyRound className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                          {!isSelf && !isMgr && (
                            <button
                              type="button"
                              onClick={() => setActiveMutation.mutate({ name: row.name, is_active: !row.is_active })}
                              disabled={setActiveMutation.isPending || (isAdmin && row.is_active)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
                            >
                              {row.is_active ? t('users.disable') : t('users.enable')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">{editingUser ? t('common.edit') : t('users.add')}</h3>
              <button type="button" onClick={() => { setShowModal(false); setEditingUser(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.fullName')}</label>
                <input className="input-field" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                <input type="email" className="input-field" value={form.email} disabled={!!editingUser}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required={!editingUser} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.username')}</label>
                  <input className={`input-field ${usernameStatus === 'unavailable' ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : usernameStatus === 'available' ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500' : ''}`} value={form.username} placeholder="Optional" disabled={!!editingUser}
                    onChange={(e) => {
                      setForm({ ...form, username: e.target.value });
                      if (usernameStatus !== 'idle') setUsernameStatus('idle');
                    }}
                    onBlur={handleUsernameBlur}
                  />
                  {usernameStatus === 'checking' && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Checking availability...
                    </p>
                  )}
                  {usernameStatus === 'available' && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <UserCheck className="w-3 h-3" /> Username is available
                    </p>
                  )}
                  {usernameStatus === 'unavailable' && (
                    <div className="mt-1">
                      <p className="text-xs text-red-600 flex items-center gap-1 mb-1">
                        <UserX className="w-3 h-3" /> Username is already taken
                      </p>
                      {usernameSuggestions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          <span className="text-xs text-gray-500">Suggestions:</span>
                          {usernameSuggestions.map(sug => (
                            <button
                              key={sug}
                              type="button"
                              onClick={() => {
                                setForm({ ...form, username: sug });
                                setUsernameStatus('available');
                              }}
                              className="text-xs bg-gray-100 hover:bg-primary-50 text-gray-700 hover:text-primary-700 px-2 py-0.5 rounded cursor-pointer transition-colors"
                            >
                              {sug}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.mobile')}</label>
                  <input
                    className="input-field"
                    value={form.mobile}
                    placeholder={isPakistan(countryCode) ? 'e.g. 03001234567' : 'e.g. 07123 456789'}
                    title={isPakistan(countryCode) ? 'Enter a valid Pakistani mobile number' : UK_PHONE_ERROR}
                    onChange={(e) => setForm({ ...form, mobile: sanitizePhoneInput(e.target.value, countryCode) })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.role')}</label>
                {form.role === ROLES.COMPANY_ADMIN || (isMgr && editingUser?.user === currentUser?.name) ? (
                  <div className="input-field bg-gray-100 text-gray-500 flex items-center cursor-not-allowed border-gray-200">
                    {form.role === ROLES.COMPANY_ADMIN ? 'Company Owner' : form.role}
                  </div>
                ) : (
                  <select className="input-field" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    {allowedAssignableRoles.map((role) => (
                      <option key={role} value={role}>
                        {translateRole(role, t)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daily Expense Limit ({getCurrencySymbol()})</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={form.daily_expense_limit}
                    onChange={(e) => setForm({ ...form, daily_expense_limit: e.target.value })}
                    placeholder="0 = no limit"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift Expense Limit ({getCurrencySymbol()})</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={form.shift_expense_limit}
                    onChange={(e) => setForm({ ...form, shift_expense_limit: e.target.value })}
                    placeholder="0 = no limit"
                  />
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-sm font-medium text-gray-800">POS staff limits</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max discount %</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      className="input-field"
                      value={form.max_discount_pct}
                      onChange={(e) => setForm({ ...form, max_discount_pct: e.target.value })}
                      placeholder="0 = inherit company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max refund per return ({getCurrencySymbol()})</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field"
                      value={form.max_refund_amount}
                      onChange={(e) => setForm({ ...form, max_refund_amount: e.target.value })}
                      placeholder="0 = unlimited"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max returns per shift</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="input-field"
                    value={form.max_refund_count}
                    onChange={(e) => setForm({ ...form, max_refund_count: e.target.value })}
                    placeholder="0 = unlimited"
                  />
                </div>
                {isCompanyAdmin(currentRole) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Fixed Salary</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{getCurrencySymbol()}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input-field pl-8"
                        value={form.salary}
                        onChange={(e) => setForm({ ...form, salary: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.can_override_min_price}
                      onChange={(e) => setForm({ ...form, can_override_min_price: e.target.checked })}
                    />
                    Can adjust cart item price
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.can_add_expenses}
                      onChange={(e) => setForm({ ...form, can_add_expenses: e.target.checked })}
                    />
                    Can add drawer expenses
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.can_process_returns}
                      onChange={(e) => setForm({ ...form, can_process_returns: e.target.checked })}
                    />
                    Can process returns / refunds
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.can_update_item_prices}
                      onChange={(e) => setForm({ ...form, can_update_item_prices: e.target.checked })}
                    />
                    Can update item prices
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.can_create_items}
                      onChange={(e) => setForm({ ...form, can_create_items: e.target.checked })}
                    />
                    Can create items from barcode scan
                  </label>
                </div>
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.password')}</label>
                  <input type="password" className="input-field" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
                </div>
              )}
              {formError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {formError}
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : (!editingUser ? <Plus className="w-4 h-4" /> : <Pencil className="w-4 h-4" />)}
                  {editingUser ? t('common.saveChanges') : t('users.create')}
                </button>
                <button type="button" onClick={() => { setShowModal(false); setEditingUser(null); }} className="btn-secondary">{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-1">{t('users.resetPassword')}</h3>
            <p className="text-sm text-gray-500 mb-4">Set a new password for {resetTarget.full_name}</p>
            <input
              type="password"
              className="input-field mb-4"
              placeholder="New password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => resetPasswordMutation.mutate()}
                disabled={resetPasswordMutation.isPending || newPassword.length < 6}
                className="btn-primary flex-1"
              >
                {resetPasswordMutation.isPending ? 'Saving...' : 'Save Password'}
              </button>
              <button type="button" onClick={() => setResetTarget(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
